/**
 * Coverage Status Badge Component
 * Displays coverage status with color-coded badges
 */

import { useLingui } from '@lingui/react';
import { AlertTriangle, CheckCircle2, XCircle, type LucideIcon } from 'lucide-react';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import { CoverageStatus, type CoverageStatusType } from '@/types/coverage';

export interface CoverageStatusBadgeProps {
  /** The coverage status to display */
  status: CoverageStatusType | string | undefined;
  /** Optional additional class names */
  className?: string;
}

/**
 * Displays the coverage status as a status badge:
 * SUFFICIENT success, WARNING warning, CRITICAL danger, the rest neutral.
 */
export function CoverageStatusBadge({
  status,
  className,
}: CoverageStatusBadgeProps) {
  const { i18n } = useLingui();

  const getStatusConfig = (
    statusValue: string | undefined
  ): { label: string; tone: StatusTone; icon?: LucideIcon } => {
    // Normalize to uppercase for case-insensitive comparison with constants
    const normalizedStatus = statusValue?.toUpperCase();
    switch (normalizedStatus) {
      case CoverageStatus.SUFFICIENT:
        return {
          label: i18n._({
            id: 'coverage.status.sufficient',
            message: 'Sufficient',
          }),
          tone: 'success',
          icon: CheckCircle2,
        };
      case CoverageStatus.WARNING:
        return {
          label: i18n._({
            id: 'coverage.status.warning',
            message: 'Warning',
          }),
          tone: 'warning',
          icon: AlertTriangle,
        };
      case CoverageStatus.CRITICAL:
        return {
          label: i18n._({
            id: 'coverage.status.critical',
            message: 'Critical',
          }),
          tone: 'danger',
          icon: XCircle,
        };
      case CoverageStatus.NO_CHILDREN:
        return {
          label: i18n._({
            id: 'coverage.status.noChildrenLabel',
            message: 'No children',
          }),
          tone: 'neutral',
        };
      case CoverageStatus.NOT_CONFIGURED:
        return {
          label: i18n._({
            id: 'coverage.status.notConfigured',
            message: 'Not Configured',
          }),
          tone: 'neutral',
        };
      default:
        return {
          label: i18n._({
            id: 'common.unknown',
            message: 'Unknown',
          }),
          tone: 'neutral',
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <StatusBadge
      tone={config.tone}
      icon={config.icon}
      label={config.label}
      className={className}
    />
  );
}
